import { Table } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Input } from '../input/input';

type GlobalSearchNewProps<TData> = {
  table: Table<TData>;
  placeholder?: string;
  className?: string;
};

/**
 * Global search input that filters across all columns.
 *
 * @param props.table - TanStack Table instance (must have getFilteredRowModel enabled)
 * @param props.placeholder - Placeholder text for the search input
 * @param props.className - Additional classes for the input
 *
 * @example
 * ```tsx
 * const [globalFilter, setGlobalFilter] = useState('');
 *
 * const table = useReactTable({
 *   data,
 *   columns,
 *   getFilteredRowModel: getFilteredRowModel(),
 *   onGlobalFilterChange: setGlobalFilter,
 *   state: { globalFilter }
 * });
 *
 * <DataTableNew.GlobalSearch
 *   table={table}
 *   placeholder="Search transactions..."
 * />
 * ```
 */
export const GlobalSearchNew = <TData,>({ table, placeholder = 'Search', className }: GlobalSearchNewProps<TData>) => {
  return (
    <Input
      placeholder={placeholder}
      aria-label={placeholder}
      value={table.getState().globalFilter ?? ''}
      onChange={(e) => {
        table.setGlobalFilter(e.target.value);
      }}
      leftIcon={<Search />}
      clearable
      onClear={() => table.setGlobalFilter('')}
      className={cn('w-72 rounded-lg border-input bg-background py-2.5', className)}
    />
  );
};
